import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    FunnelGeneration,
    FunnelGenerationRequest,
    FunnelSlide,
    FunnelSlidePrompt,
    AIService,
    PromptTemplate,
    FunnelSlideRegenerateRequest
} from '../types';

import {
    startFunnelGeneration,
    triggerFunnelSlideGeneration,
    getFunnelGenerationStatus,
    regenerateFunnelSlide,
} from '../services/api';

interface UseFunnelGenerationProps {
    selectedProductId: number | '';
    selectedAIServiceCode: string;
    selectedSlideServiceCode: string;
    selectedPromptTemplateId: number | '';
    slidesCount: number;
    funnelType: string;
    targetMarketplace: string;
    language: string;
    additionalInstructions: string;
    selectedReferenceImageUrl: string | null;
    allAiServices?: AIService[];
    promptTemplates?: PromptTemplate[];
}

export const useFunnelGeneration = ({
                                        selectedProductId,
                                        selectedAIServiceCode,
                                        selectedSlideServiceCode,
                                        selectedPromptTemplateId,
                                        slidesCount,
                                        funnelType,
                                        targetMarketplace,
                                        language,
                                        additionalInstructions,
                                        selectedReferenceImageUrl,
                                        allAiServices,
                                        promptTemplates,
                                    }: UseFunnelGenerationProps) => {
    const [generationId, setGenerationId] = useState<number | null>(null);

    // ✅ ИСПРАВЛЕНИЕ: Добавлен 'slides_failed' статус
    const [generationStep, setGenerationStep] = useState<
        'idle' | 'prompt_generating' | 'prompt_ready' | 'slides_generating' | 'completed' | 'failed' | 'slides_failed'
    >('idle');

    const [slidePrompts, setSlidePrompts] = useState<FunnelSlidePrompt[]>([]);
    const [generatedSlides, setGeneratedSlides] = useState<FunnelSlide[]>([]);
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const selectedPromptTemplate = useMemo(() => {
        return promptTemplates?.find(t => t.id === selectedPromptTemplateId);
    }, [promptTemplates, selectedPromptTemplateId]);

    const selectedAIService = useMemo(() => {
        return allAiServices?.find(s => s.code === selectedAIServiceCode);
    }, [allAiServices, selectedAIServiceCode]);

    const selectedSlideService = useMemo(() => {
        return allAiServices?.find(s => s.code === selectedSlideServiceCode);
    }, [allAiServices, selectedSlideServiceCode]);

    const promptGenMutation = useMutation({
        mutationFn: () => {
            if (!selectedProductId || !selectedReferenceImageUrl) {
                throw new Error("Product and Reference Image must be selected.");
            }
            const request: FunnelGenerationRequest = {
                reference_image_url: selectedReferenceImageUrl,
                slides_count: slidesCount,
                funnel_type: funnelType as any,
                target_marketplace: targetMarketplace as any,
                language: language,
                ai_service: selectedAIServiceCode,
                slide_ai_service: selectedSlideServiceCode,
                prompt_template_id: selectedPromptTemplateId as number,
                extra_instructions: additionalInstructions,
            };
            return startFunnelGeneration(selectedProductId, request);
        },
        onSuccess: (data: { id: number; status: string }) => {
            setGenerationId(data.id);
            setGenerationStep('prompt_generating');
            setError(null);
        },
        onError: (e: any) => {
            setError(e.message || 'Failed to start funnel generation');
            setGenerationStep('failed');
        },
    });

    const generationStatusQuery = useQuery<FunnelGeneration>({
        queryKey: ['funnelGenerationStatus', generationId],
        queryFn: () => getFunnelGenerationStatus(selectedProductId as number, generationId as number),
        enabled: !!generationId,
        refetchInterval: (data) => {
            if (!data) return 2000;
            // ✅ ИСПРАВЛЕНИЕ: Добавлен 'slides_failed' в активные статусы для продолжения polling
            const isActive = data.status === 'prompt_generating' ||
                data.status === 'slides_generating' ||
                data.status === 'slides_failed' ||
                data.status === 'pending';
            return isActive ? 3000 : false;
        },
    });

    useEffect(() => {
        const data = generationStatusQuery.data;
        if (!data) return;

        setGenerationStep(data.status as any);

        if (data.slide_prompts) {
            setSlidePrompts(data.slide_prompts);
        }

        if (data.status === 'slides_generating' && data.slide_requests) {
            const newSlidesData = data.slide_requests;
            setGeneratedSlides(currentSlides => {
                const updatedSlides = [...currentSlides];
                newSlidesData.forEach(newSlide => {
                    const index = updatedSlides.findIndex(s => s.position === newSlide.position);
                    if (index !== -1) {
                        updatedSlides[index] = newSlide;
                    }
                });
                return updatedSlides;
            });
        } else if (data.status === 'completed' && data.slide_requests) {
            setGeneratedSlides(data.slide_requests);
            // ✅ ИСПРАВЛЕНИЕ: Очищаем ошибку при успешном завершении
            setError(null);
        } else if (data.status === 'slides_failed' && data.slide_requests) {
            // ✅ НОВЫЙ БЛОК: Обработка частичного успеха
            setGeneratedSlides(data.slide_requests);
            const failedCount = data.slide_requests.filter(s => s.status === 'failed').length;
            const successCount = data.slide_requests.filter(s => s.status === 'completed').length;
            setError(data.error_message || `${successCount} slides generated successfully, ${failedCount} failed. You can retry failed slides or regenerate prompts.`);
        } else if (data.status === 'failed') {
            setError(data.error_message || 'Generation failed');
        }
    }, [generationStatusQuery.data]);

    const slideGenMutation = useMutation({
        mutationFn: () => triggerFunnelSlideGeneration(selectedProductId as number, generationId as number),
        onSuccess: () => {
            const initialSlides: FunnelSlide[] = Array.from({ length: slidesCount }, (_, i) => ({
                // ✅ ИСПРАВЛЕНО: `prompt` теперь строка, и убраны лишние поля
                id: -i,
                position: i + 1,
                status: 'pending',
                image_url: null,
                slide_type: 'Pending',
                title: 'Waiting...',
                description: '',
                prompt: 'Waiting for prompt generation...', // `prompt` должен быть строкой
            }));
            setGeneratedSlides(initialSlides);
            setGenerationStep('slides_generating');
            setError(null);
            queryClient.invalidateQueries({ queryKey: ['funnelGenerationStatus', generationId] });
        },
        onError: (e: any) => {
            setError(e.message || 'Failed to start slide generation');
        },
    });

    const slideRegenerateMutation = useMutation({
        mutationFn: ({ slidePosition, request }: { slidePosition: number; request: FunnelSlideRegenerateRequest }) =>
            regenerateFunnelSlide(selectedProductId as number, generationId as number, slidePosition, request),
        onSuccess: (data) => {
            if (data && data.slide_requests) {
                setGeneratedSlides(data.slide_requests);
            }
            queryClient.invalidateQueries({ queryKey: ['funnelGenerationStatus', generationId] });
        },
        onError: (e: any) => {
            setError(e.message || 'Failed to regenerate slide');
        },
    });

    const resetGeneration = () => {
        setGenerationId(null);
        setGenerationStep('idle');
        setSlidePrompts([]);
        setGeneratedSlides([]);
        setError(null);
    };

    const handleLoadGeneration = (gen: FunnelGeneration) => {
        setGenerationId(gen.id);
        setGenerationStep(gen.status as any);
        setSlidePrompts(gen.slide_prompts || []);
        setGeneratedSlides(gen.slide_requests || []);
    };

    const canStartPromptGen =
        selectedProductId !== '' &&
        selectedAIServiceCode !== '' &&
        selectedSlideServiceCode !== '' &&
        selectedPromptTemplateId !== '' &&
        selectedReferenceImageUrl !== null &&
        generationStep === 'idle';

    // ✅ ИСПРАВЛЕНИЕ: Добавлен 'slides_failed' как разрешенный статус для старта генерации слайдов
    const canStartSlideGen =
        (generationStep === 'prompt_ready' || generationStep === 'slides_failed') &&
        slidePrompts.length > 0 &&
        generationId !== null;

    // ✅ НОВЫЕ ФЛАГИ для UI компонентов:
    const canRetryFailedSlides = generationStep === 'slides_failed';

    const hasPartialSuccess = generationStep === 'slides_failed' &&
        generatedSlides.some(s => s.status === 'completed');

    const failedSlidesCount = generatedSlides.filter(s => s.status === 'failed').length;

    const completedSlidesCount = generatedSlides.filter(s => s.status === 'completed').length;

    // ✅ НОВАЯ ФУНКЦИЯ: Получить только неудавшиеся слайды для отображения
    const getFailedSlides = () => {
        return generatedSlides.filter(s => s.status === 'failed');
    };

    // ✅ НОВАЯ ФУНКЦИЯ: Получить только успешные слайды для отображения
    const getCompletedSlides = () => {
        return generatedSlides.filter(s => s.status === 'completed');
    };

    // ✅ НОВАЯ ФУНКЦИЯ: Проверить можно ли регенерировать промпты
    const canRegeneratePrompts = generationStep !== 'idle' &&
        generationStep !== 'prompt_generating' &&
        generationId !== null;

    return {
        generationId,
        currentGeneration: generationStatusQuery.data,
        generationStep,
        slidePrompts,
        generatedSlides,
        error,
        selectedPromptTemplate,
        selectedAIService,
        selectedSlideService,
        promptGenMutation,
        slideGenMutation,
        slideRegenerateMutation,
        resetGeneration,
        handleLoadGeneration,
        canStartPromptGen,
        canStartSlideGen,

        // ✅ НОВЫЕ ВОЗВРАЩАЕМЫЕ ЗНАЧЕНИЯ для улучшенного UI:
        canRetryFailedSlides,
        hasPartialSuccess,
        failedSlidesCount,
        completedSlidesCount,
        getFailedSlides,
        getCompletedSlides,
        canRegeneratePrompts,
    };
};
